import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../generic-components/Layout';
import ContentWrapper from '../../generic-components/ContentWrapper';
import { APP_LAYOUT_STYLES } from '../../generic-components/Layout/constants';
import {
  convertFilterToQueryString,
  getInitialTransactionsFilters,
  getDetailsComponentsToShow,
  getExtendedTransaction,
} from '../transactions/utils';
import {
  CURRENCY,
  DATE_FORMAT,
  EMERGENCY_BUCKET_TRANSACTIONS_HEADERS,
  FEATURE_FLAG,
  FORMAT_TYPE,
} from '../../constants/general';
import MainContent from '../../generic-components/Layout/MainContent';
import SubContent from '../../generic-components/Layout/SubContent';
import 'react-loading-skeleton/dist/skeleton.css';
import { IAdditionalTransactionsStates, ITransaction, ITransactionDetailsMap } from '../../types';
import TransactionDetails from '../transactions/components/TransactionDetails';
import { useStore } from '../../store';
import { formatAmount } from '../../helpers/currency.helper';
import { refactoredTimeAndDate } from '../../helpers/timeFormatter.helper';
import { Table } from '../../generic-components/Table';
import { TRANSACTION_DETAILS_DATA, TRANSACTIONS_TABLE_HEADERS } from '../transactions/constants';
import { useEmergencyBucketTransactionsQuery } from '../../hooks/query/useEmergencyBucketTransactionsQuery';
import BackIcon from '../../assets/svg/backArrow.svg';
import { validateFeatureFlag } from '../../services/featureFlagService';

const EmergencyBucketTransactions = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { customerId } = useStore((state) => state.user);
  const { getPrimaryAccount } = useStore((state) => state);
  const summaryData = useStore((state) => state.summary);
  const { BASIC, PRO } = EMERGENCY_BUCKET_TRANSACTIONS_HEADERS;
  const setActiveTransaction = useStore((state) => state.setActiveTransaction);
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [current, setCurrent] = useState<string>('');
  const [shouldCleanTransactions, setShouldCleanTransactions] = useState<boolean>(false);
  const [detailsComponentsMap, setDetailsComponentsMap] = useState<ITransactionDetailsMap | null>(null);
  const [filterObject, setFilterObject] = useState(getInitialTransactionsFilters());
  const initialQuery = convertFilterToQueryString(filterObject);
  const [extendedTransaction, setExtendedTransaction] = useState<{} | null>(null);
  const [query, setQuery] = useState<string>(initialQuery);
  const [isPreselectedActivated, setIsPreselectedActivated] = useState<boolean>(false);
  const [additionalTransactionsStates, setAdditionalTransactionsStates] = useState<IAdditionalTransactionsStates>({
    endOfTransactions: false,
    noTransactionsAtAll: false,
  });
  const isEmergencyBucketActive =
    validateFeatureFlag(FEATURE_FLAG.EMERGENCY_BUCKET) && summaryData.emergencyBucketResponse?.active;
  const {
    data: transactionsData,
    refetch: refetchTransactions,
    isSuccess,
    isLoading,
  } = useEmergencyBucketTransactionsQuery({ customerId, query, enabled: isEmergencyBucketActive });

  const activeTransaction = useMemo(() => {
    const trx = transactions.find((t) => t.transactionId === current);
    if (trx) {
      return trx;
    }
    return null;
  }, [current, transactions]);

  const activeTransactionIndex = useMemo(
    () => transactions.findIndex((t) => t.transactionId === current),
    [current, transactions],
  );
  const tableItems = () => {
    const uniqueTransactions = Array.from(
      new Map(transactions.map(trx => [trx.transactionId, trx])).values(),
    );
    return useMemo(
      () =>
        uniqueTransactions.map(
          ({
            timestamp,
            details,
            transactionStatus,
            amount = '0',
            transactionId,
            merchantName,
            currencyCode = CURRENCY.USD,
            title,
            billerName,
            prettyName,
            trxTitle,
          }) => ({
            date: refactoredTimeAndDate(timestamp, null, DATE_FORMAT.LETTERS).toString(),
            name: trxTitle || title || merchantName || billerName || prettyName || details,
            status: transactionStatus,
            amount: formatAmount(
              amount,
              FORMAT_TYPE.FORMATTED,
              CURRENCY[currencyCode as keyof typeof CURRENCY].SYMBOL,
              true,
            ),
            transactionId,
          }),
        ),
      [transactions],
    );
  };

  const handleTableItemClicked = ({ transactionId }: ITransaction) => {
    if (!transactionId) return;
    setCurrent(transactionId);
  };

  const handlePagination = () => {
    if (!transactionsData) return;
    if (filterObject.page) {
      if (transactionsData.numOfTransactions < TRANSACTION_DETAILS_DATA.MAX_RECORD_COUNT) {
        if (filterObject.page > 1) {
          setAdditionalTransactionsStates((prevState) => ({ ...prevState, endOfTransactions: true }));
        } else {
          setAdditionalTransactionsStates((prevState) => ({ ...prevState, endOfTransactions: false }));
        }
        return;
      }
      const next = filterObject.page + 1;
      setFilterObject((prevState) => ({ ...prevState, page: next }));
    }
  };

  const handleOnSuccessUpload = (comment: string) => {
    if (comment === null || comment === undefined) {
      return;
    }
    const newState = [...transactions];
    const currentTransaction = newState.find((e) => e.transactionId === current);
    if (currentTransaction && Object.keys(currentTransaction).length > 0) {
      currentTransaction.comment = comment;
      setTransactions(newState);
    }
  };

  // Get transactions once query is updated
  useEffect(() => {
    if (isEmergencyBucketActive) {
      refetchTransactions();
    }
  }, [query]);

  // Once transactionsData.transactionList is updated we set the current and populate the transaction state
  useEffect(() => {
    if (transactionsData && isSuccess) {
      if (transactionsData.numOfTransactions) {
        setAdditionalTransactionsStates((prevState) => ({
          ...prevState,
          endOfTransactions: false,
          thereIsTransactions: true,
        }));
        if (shouldCleanTransactions) {
          setTransactions([...transactionsData.transactionList]);
          setShouldCleanTransactions(false);
        } else {
          setTransactions((prevState) => [...prevState, ...transactionsData.transactionList]);
        }
        if (locationState?.preSelectedId && !isPreselectedActivated) {
          setCurrent(locationState.preSelectedId);
          setIsPreselectedActivated(true);
        } else if (!current) {
          setCurrent(transactionsData.transactionList[0].transactionId);
        }
      }
    }
  }, [transactionsData]);

  // Once current transaction is updated we update store as well
  useEffect(() => {
    if (!current || !activeTransaction) return;
    setActiveTransaction(activeTransaction);
    const map = getDetailsComponentsToShow(activeTransaction);
    if (map) {
      setDetailsComponentsMap(map);
    }
  }, [current]);

  // Once filter object is changes we need to create a new query param
  useEffect(() => {
    if (!filterObject) return;
    setQuery(convertFilterToQueryString(filterObject));
  }, [filterObject]);

  useEffect(
    () => () => {
      setTransactions([]);
    },
    [],
  );

  useEffect(() => {
    if (!activeTransaction) return;
    (async () => {
      const transactionData = await getExtendedTransaction({
        activeTransaction,
        customerId,
        accountNo: getPrimaryAccount().achAccountNumber,
      });
      transactions[activeTransactionIndex] = transactionData;
      setExtendedTransaction(transactionData);
    })();
  }, [activeTransaction]);

  const { t } = useTranslation();

  return (
    <AppLayout
      assetPath={BackIcon}
      backButtonStyle='flex items-center justify-center w-[2rem] h-[2rem]'
      navBarStyle={`${APP_LAYOUT_STYLES.NAVBAR.LOGIN_FLOW_STYLE} w-[744px] mt-[1rem] mb-4`}
      title={t(validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT) ? PRO : BASIC)}
      titleStyle='font-metricMedium text-xl'
      layoutStyle={`${APP_LAYOUT_STYLES.LAYOUT} gap-0`}
      handleNavigate={() => {
        navigate('/emergency-bucket-dashboard/:main');
      }}
    >
      <ContentWrapper>
        <MainContent className='relative bg-bw00 rounded-lg w-[744px] max-h-[65.6vh] select-none'>
          <Table
            headers={TRANSACTIONS_TABLE_HEADERS}
            items={tableItems() || []}
            tableType='sortable'
            onItemClick={handleTableItemClicked}
            handleLastItemIntersecting={handlePagination}
            additionalStates={additionalTransactionsStates}
            isSuccess={isSuccess}
            isLoading={isLoading}
          />
        </MainContent>
        {extendedTransaction && tableItems.length > 0 && !isLoading ? (
          <SubContent className='bg-bw00 rounded-lg p-10 h-min max-h-[35.5rem] tablet:w-[300px] pc-sm:w-[360px] pc-md:max-h-[38.5rem]'>
            <TransactionDetails
              handleOnSuccessUpload={handleOnSuccessUpload}
              detailsComponentsMap={detailsComponentsMap}
              detailsDataObject={extendedTransaction}
            />
          </SubContent>
        ) : (
          isLoading && (
            <SubContent className='bg-bw00 rounded-lg p-10 h-min max-h-[35.5rem] tablet:w-[300px] pc-sm:w-[360px] pc-md:max-h-[38.5rem]'>
              <div className='w-full'>
                <Skeleton className='mb-8' count={7} />
              </div>
            </SubContent>
          )
        )}
      </ContentWrapper>
    </AppLayout>
  );
};

export default EmergencyBucketTransactions;
